import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faBookOpen, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import guides from './guides/guideContent';
import './GuidePage.css';

function GuidePage() {
  const { guideSlug } = useParams();
  const guideIndex = guides.findIndex(g => g.slug === guideSlug);

  if (guideIndex === -1) {
    return <Navigate to="/guides" replace />;
  }

  const guide = guides[guideIndex];
  const prevGuide = guideIndex > 0 ? guides[guideIndex - 1] : null;
  const nextGuide = guideIndex < guides.length - 1 ? guides[guideIndex + 1] : null;

  return (
    <div className="guide-page">
      <div className="guide-header">
        <div className="container">
          <div className="guide-header-content">
            <div className="guide-header-left">
              <Link to="/guides" className="guide-back-btn" title="All Guides">
                <FontAwesomeIcon icon={faArrowLeft} />
              </Link>
              <div>
                <h1 className="guide-header-title">
                  <FontAwesomeIcon icon={guide.icon} style={{ marginRight: '0.5rem' }} />
                  {guide.title}
                </h1>
                <p className="guide-header-subtitle">{guide.description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Quick Links to All Guides */}
        <div className="guide-quick-links">
          {guides.map((g) => (
            <Link
              to={`/guides/${g.slug}`}
              className={`guide-quick-link ${g.slug === guideSlug ? 'guide-quick-link-active' : ''}`}
              key={g.slug}
            >
              <FontAwesomeIcon icon={g.icon} />
              <span>{g.title.replace('with Church Aware', '').replace('Members into ', '').trim()}</span>
            </Link>
          ))}
        </div>

        {/* Two-column layout: sidebar TOC + main steps */}
        <div className="guide-layout">
          {/* Left sidebar: Table of Contents */}
          <aside className="guide-sidebar">
            <div className="guide-toc">
              <h3 className="guide-toc-title">
                <FontAwesomeIcon icon={faBookOpen} style={{ marginRight: '0.5rem', fontSize: '0.8rem' }} />
                In this guide
              </h3>
              <ol className="guide-toc-list">
                {guide.sections.map((section, i) => (
                  <li key={i}>
                    <a href={`#section-${i}`} className="guide-toc-link">{section.title}</a>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Right column: Steps */}
          <div className="guide-main">
            {guide.sections.map((section, i) => (
              <div className="guide-section" key={i} id={`section-${i}`}>
                <div className="guide-step-number">{i + 1}</div>
                <div className="guide-step-body">
                  <h2 className="guide-step-title">{section.title}</h2>
                  <p className="guide-step-text">{section.content}</p>
                  {section.link && (
                    <Link to={section.link.path} className="guide-step-link">
                      {section.link.text} <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '0.75rem' }} />
                    </Link>
                  )}
                </div>
              </div>
            ))}

            {/* Action Links Section */}
            {guide.actionLinks && guide.actionLinks.length > 0 && (
              <div className="guide-actions">
                <h3 className="guide-actions-title">Ready to get started?</h3>
                <p className="guide-actions-desc">Jump straight to the pages mentioned in this guide:</p>
                <div className="guide-actions-links">
                  {guide.actionLinks.map((action, i) => (
                    <Link to={action.path} className="guide-action-btn" key={i}>
                      <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.75rem' }} />
                      {action.text}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="guide-nav">
              {prevGuide ? (
                <Link to={`/guides/${prevGuide.slug}`} className="guide-nav-link guide-nav-prev">
                  <FontAwesomeIcon icon={faArrowLeft} />
                  <div>
                    <span className="guide-nav-label">Previous</span>
                    <span className="guide-nav-title">{prevGuide.title}</span>
                  </div>
                </Link>
              ) : <div />}
              {nextGuide ? (
                <Link to={`/guides/${nextGuide.slug}`} className="guide-nav-link guide-nav-next">
                  <div>
                    <span className="guide-nav-label">Next</span>
                    <span className="guide-nav-title">{nextGuide.title}</span>
                  </div>
                  <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              ) : (
                <Link to="/guides" className="guide-nav-link guide-nav-next">
                  <div>
                    <span className="guide-nav-label">Back to</span>
                    <span className="guide-nav-title">All Guides</span>
                  </div>
                  <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GuidePage;
